import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import axios from "../../config/axios";
import { toast } from "react-toastify";

function AddAdmin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    role: "admin",
    status: "active",
  });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/;
    const emailRegex =
      /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;

    if (!formData.email || !emailRegex.test(formData.email))
      newErrors.email = "Valid email is required";
    if (!formData.password || !passwordRegex.test(formData.password))
      newErrors.password =
        "Password must be minimum 8 characters long and should contain atleast one uppercase, one lowercase and one special character.";
    if (!formData.role) newErrors.role = "Role is required";
    if (!formData.status) newErrors.status = "Status is required";

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setFormErrors({});

      const response = await axios.post("/admin/add", formData);

      // Navigate back to admin list
      navigate("/admin", {
        state: {
          message: "Admin created successfully",
          severity: "success",
        },
      });
      toast.success(response.data.message);
    } catch (error) {
      console.error("Add admin error:", error);
      toast.error(error.response?.data?.message || "Error creating admin");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={3}
        sx={{ p: 4, maxWidth: 750, mx: "auto", borderRadius: "12px" }}
      >
        <h1
          style={{
            color: "#263238",
            fontSize: "28px",
            fontWeight: 600,
            marginBottom: "20px",
            marginTop: "-10px",
            textAlign: "center",
          }}
        >
          Add Admin
        </h1>

        <form onSubmit={handleSubmit}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 3,
              mb: 4,
            }}
          >
            <TextField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              error={!!formErrors.email}
              helperText={formErrors.email}
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  borderRadius: "8px",
                  background: "#F5F5F5",
                },
                "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#26A69A",
                },
              }}
            />

            <TextField
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              error={!!formErrors.password}
              helperText={formErrors.password}
              fullWidth
              sx={{
                "& .MuiInputBase-root": {
                  borderRadius: "8px",
                  background: "#F5F5F5",
                },
                "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#26A69A",
                },
              }}
            />

            <FormControl fullWidth error={!!formErrors.role}>
              <InputLabel>Role</InputLabel>
              <Select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                label="Role"
                sx={{
                  borderRadius: "8px",
                  background: "#F5F5F5",
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#26A69A",
                  },
                }}
              >
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
              {formErrors.role && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 1, display: "flex", alignItems: "center" }}
                >
                  <ErrorIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  {formErrors.role}
                </Typography>
              )}
            </FormControl>
            <FormControl fullWidth error={!!formErrors.status}>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                label="Status"
                sx={{
                  borderRadius: "8px",
                  background: "#F5F5F5",
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#26A69A",
                  },
                }}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
              {formErrors.status && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 1, display: "flex", alignItems: "center" }}
                >
                  <ErrorIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  {formErrors.status}
                </Typography>
              )}
            </FormControl>
          </Box>

          <div
            style={{
              gridColumn: "span 2",
              display: "flex",
              justifyContent: "flex-end",
              gap: "16px",
              marginTop: "24px",
            }}
          >
            <Button
              onClick={() => navigate("/admin")}
              style={{
                background: "#FFFFFF",
                color: "#263238",
                padding: "10px 20px",
                borderRadius: "8px",
                border: "2px solid #263238",
                fontWeight: 500,
                textTransform: "none",
                transition: "all 0.3s",
              }}
              sx={{
                "&:hover": {
                  background: "#263238",
                  color: "#FFFFFF",
                  transform: "scale(1.02)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              style={{
                background: "#FF6F61",
                color: "#FFFFFF",
                padding: "10px 20px",
                borderRadius: "8px",
                fontWeight: 500,
                textTransform: "none",
                transition: "all 0.3s",
              }}
              sx={{
                "&:hover": {
                  background: "#F4511E",
                  transform: "scale(1.02)",
                },
                "&:disabled": {
                  background: "#B0BEC5",
                  cursor: "not-allowed",
                },
              }}
            >
              {loading ? "loading..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </Paper>
    </Box>
  );
}

export default AddAdmin;
